import React, { useEffect, useState, useMemo } from "react";
import styles from "./summaryMeeting.module.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import i18n from "../../../i18n";
import edit from "../../../images/edit_icon_blue.svg";
import copy from "../../../images/copy_icon.png";
import { TTextAnalysis, MeetingRecordStatus, TStructuredMeetingSummary, ProcessingStages, ProcessingStageSteps, TProcessingStage, TMeetingRecord, TAutoFillActionReportResponse } from "../../../services/types/user.types";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import TextAnalysisInPopup from "../../textAnalysisInPopup/textAnalysisInPopup";
import useMediaQuery from "../../../hooks/useMediaQuery";
import VoiceAnalysisInPopup from "../../voiceAnalysisInPopup/voiceAnalysisInPopup";
import { useGetUserMeetingRecordsMutation, useUpdateMeetingSummaryMutation, useExportMeetingSummaryToPdfMutation, useReprocessMeetingRecordStageMutation } from "../../../services/api/user.api";
import Modal from "../../modals/modal/Modal";
import QuestionnaireSelectionModal from "../../modals/questionnaireSelectionModal/QuestionnaireSelectionModal";

// Format duration in seconds to mm:ss or hh:mm:ss
function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0) {
    return `${hrs}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

// Format date to dd/mm/yyyy (converts UTC to local)
function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateStr;
  }
}

// Format time to HH:mm (converts UTC to local)
// For new records: date field contains full UTC timestamp → convert to local
// For legacy records: date is midnight UTC → combine with utcTime (HH:mm) fallback → convert to local
function formatTime(dateStr: string | null | undefined, utcTime?: string | null): string {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";
    const isMidnight = date.getUTCHours() === 0 && date.getUTCMinutes() === 0 && date.getUTCSeconds() === 0;
    if (!isMidnight) {
      // Full UTC timestamp — convert to local
      const hours = String(date.getHours()).padStart(2, "0");
      const mins = String(date.getMinutes()).padStart(2, "0");
      return `${hours}:${mins}`;
    }
    // Midnight UTC — try combining date portion with the separate UTC time string
    if (utcTime) {
      const datePart = dateStr.split("T")[0];
      const combined = new Date(`${datePart}T${utcTime}:00Z`);
      if (!isNaN(combined.getTime())) {
        const hours = String(combined.getHours()).padStart(2, "0");
        const mins = String(combined.getMinutes()).padStart(2, "0");
        return `${hours}:${mins}`;
      }
    }
    return "";
  } catch {
    return "";
  }
}

// Localize speaker label: replace "Speaker X" with the i18n equivalent (e.g., "דובר X")
function localizeSpeaker(speaker: string): string {
  // Match English "Speaker N" pattern and replace with localized version
  const englishSpeakerMatch = speaker.match(/^Speaker\s+(\d+)$/i);
  if (englishSpeakerMatch) {
    return `${t("SPEAKER")} ${englishSpeakerMatch[1]}`;
  }
  return speaker;
}

// Parse transcript and format into structured [timestamp][speaker][content] blocks.
// Supports:
//   - Gemini range format: "[MM:SS - MM:SS] SpeakerName: text" (with or without markdown bold **)
//   - Gemini legacy format: "[MM:SS] SpeakerName: text"
//   - Azure format: "[דובר X]" on its own line, followed by text lines
//   - Classic format: "SpeakerName: text" (no timestamp)
//   - Plain text fallback
function formatTranscript(transcript: string): React.ReactNode[] {
  if (!transcript) return [];
  
  // Strip markdown bold markers that Gemini sometimes adds
  const cleaned = transcript.replace(/\*\*/g, '');
  
  // Split by newlines and filter empty lines
  const lines = cleaned.split(/\n+/).filter(line => line.trim());
  const elements: React.ReactNode[] = [];
  
  // Regex for timestamp at start: [MM:SS] or [HH:MM:SS], optionally with range [MM:SS - MM:SS]
  const timestampRegex = /^\[(\d{1,2}:\d{2}(?::\d{2})?(?:\s*-\s*\d{1,2}:\d{2}(?::\d{2})?)?)\]\s*/;
  
  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();
    
    // 1. Check if line starts with a timestamp [MM:SS]
    const tsMatch = line.match(timestampRegex);
    if (tsMatch) {
      const timestamp = tsMatch[1];
      const afterTimestamp = line.substring(tsMatch[0].length).trim();
      
      // Check if there's a speaker label after the timestamp: "SpeakerName: text"
      const speakerMatch = afterTimestamp.match(/^([^:]{1,60}):\s*(.+)/);
      if (speakerMatch) {
        const speaker = localizeSpeaker(speakerMatch[1].trim());
        const text = speakerMatch[2];
        elements.push(
          <div key={elements.length} className={styles.speakerBlock}>
            <div className={styles.speakerHeader}>
              <span className={styles.timestamp}>{timestamp}</span>
              <span className={styles.speakerLabel}>{speaker}</span>
            </div>
            <div className={styles.speakerText}>{text}</div>
          </div>
        );
      } else {
        // Timestamp but no speaker colon — just timestamped text
        elements.push(
          <div key={elements.length} className={styles.speakerBlock}>
            <div className={styles.speakerHeader}>
              <span className={styles.timestamp}>{timestamp}</span>
            </div>
            <div className={styles.speakerText}>{afterTimestamp}</div>
          </div>
        );
      }
      i++;
      continue;
    }
    
    // 2. Azure format: [דובר X] on its own line, followed by text lines
    const azureSpeakerMatch = line.match(/^\[([^\]]+)\]$/);
    if (azureSpeakerMatch) {
      const speaker = localizeSpeaker(azureSpeakerMatch[1].trim());
      const textLines: string[] = [];
      
      // Collect all following lines until the next speaker label
      i++;
      while (i < lines.length) {
        const nextLine = lines[i].trim();
        // Stop if we hit another speaker label or timestamp
        if (nextLine.match(/^\[([^\]]+)\]$/) || nextLine.match(timestampRegex) || nextLine.match(/^([^:]{1,60}):\s*.+/)) {
          break;
        }
        if (nextLine) {
          textLines.push(nextLine);
        }
        i++;
      }
      
      elements.push(
        <div key={elements.length} className={styles.speakerBlock}>
          <div className={styles.speakerHeader}>
            <span className={styles.speakerLabel}>{speaker}</span>
          </div>
          <div className={styles.speakerText}>{textLines.join(' ')}</div>
        </div>
      );
      continue;
    }
    
    // 3. Classic format: "Speaker Name: text" on same line (no timestamp)
    const classicSpeakerMatch = line.match(/^([^:]{1,60}):\s*(.+)/);
    if (classicSpeakerMatch) {
      const speaker = localizeSpeaker(classicSpeakerMatch[1].trim());
      const text = classicSpeakerMatch[2];
      elements.push(
        <div key={elements.length} className={styles.speakerBlock}>
          <div className={styles.speakerHeader}>
            <span className={styles.speakerLabel}>{speaker}</span>
          </div>
          <div className={styles.speakerText}>{text}</div>
        </div>
      );
      i++;
      continue;
    }
    
    // 4. Plain text without speaker
    if (line) {
      elements.push(
        <p key={elements.length} className={styles.transcriptParagraph}>
          {line}
        </p>
      );
    }
    i++;
  }
  
  return elements;
}

// Session storage key for persisting user details across page refresh
const RECORDINGS_TABLE_USER_KEY = "recordingsTableUserDetails";

// Helper to get user details from sessionStorage
function getStoredUserDetails(): Record<string, any> | null {
  try {
    const stored = sessionStorage.getItem(RECORDINGS_TABLE_USER_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

// Helper function to parse and update all state from a record
function parseRecordData(record: any) {
  // Parse structured summary
  let structuredSummary: TStructuredMeetingSummary | null = null;
  const rawSummary = record.meetingSummary || "";
  if (rawSummary) {
    try {
      let jsonStr = rawSummary;
      if (jsonStr.includes("```json")) {
        jsonStr = jsonStr.replace(/```json\s*/g, "").replace(/```\s*/g, "");
      } else if (jsonStr.includes("```")) {
        jsonStr = jsonStr.replace(/```\s*/g, "");
      }
      const jsonMatch = jsonStr.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]) as TStructuredMeetingSummary;
        if (parsed.subject !== undefined || parsed.primaryFindings !== undefined) {
          structuredSummary = parsed;
        }
      }
    } catch { /* ignore */ }
  }

  // Parse text data
  let textData: TTextAnalysis | null = null;
  const rawTextAnalysis = record.textAnalyze;
  if (rawTextAnalysis) {
    try {
      const matchResult = rawTextAnalysis?.match?.(/\{[\s\S]*\}/);
      const jsonString = matchResult ? matchResult[0] : "{}";
      textData = JSON.parse(jsonString) as TTextAnalysis;
    } catch { /* ignore */ }
  }

  // Parse voice data
  let voiceData: any = null;
  const voiceStr = record.audioAnalyze;
  if (voiceStr) {
    try { voiceData = JSON.parse(voiceStr); } catch { /* ignore */ }
  }

  // Parse option for report
  let optionForReport = "";
  const optStr = record.optionsForReport;
  if (optStr) {
    try {
      const jsonMatch = optStr?.match?.(/\{[\s\S]*\}/);
      optionForReport = JSON.parse(jsonMatch ? jsonMatch[0] : optStr);
    } catch { /* ignore */ }
  }

  return {
    meetingSummary: rawSummary,
    structuredSummary,
    transcript: (record.transcription || "").trim(),
    textAnalysis: record.textAnalyze || "",
    voiceAnalysis: record.audioAnalyze || "",
    optionForReport,
    textData,
    voiceData,
    processingStage: record.processingStage || null,
    recordStatus: record.status || MeetingRecordStatus.COMPLETE,
    recordDuration: record.duration ?? null,
    recordDate: record.date || "",
    recordTime: formatTime(record.date, record.time) || "",
    recordSubject: record.subject || "",
    therapistName: record.therapistName || "",
    mediaType: record.type || "",
    appointmentId: record.appointmentId ?? null,
    appointmentStartTime: record.appointmentStartTime || null,
    appointmentDate: record.appointmentDate || null,
    therapistQuestionnaireCompaniesScoreUsersId:
      record.therapistQuestionnaireCompaniesScoreUsersId != null
        ? Number(record.therapistQuestionnaireCompaniesScoreUsersId)
        : null,
    cycle:
      record.cycle != null
        ? Number(record.cycle)
        : record.Cycle != null
          ? Number(record.Cycle)
          : null,
    questionnaireCompaniesId: (() => {
      const raw = record.questionnaireCompaniesId;
      if (raw == null) return undefined;
      const n = Number(raw);
      return Number.isFinite(n) ? n : undefined;
    })(),
  };
}

// Helper function to check if patientStatus has any content
function hasPatientStatusContent(patientStatus: any): boolean {
  if (!patientStatus) return false;
  return !!(
    patientStatus.currentCondition ||
    patientStatus.progressNotes ||
    patientStatus.concerns
  );
}

// Helper function to check if treatmentPlan has any content
function hasTreatmentPlanContent(treatmentPlan: any): boolean {
  if (!treatmentPlan) return false;
  return !!(
    treatmentPlan.current ||
    treatmentPlan.modifications ||
    treatmentPlan.nextSteps
  );
}

// Get current step number from processing stage
function getCurrentStep(stage: TProcessingStage | null | undefined): number {
  if (!stage) return 0;
  return ProcessingStageSteps[stage] || 0;
}

// Get translated label for a processing stage
function getStageLabel(stage: TProcessingStage | null | undefined): string {
  if (!stage) return "";
  const stageLabels: Record<string, string> = {
    [ProcessingStages.UPLOADING]: t("STAGE_UPLOADING"),
    [ProcessingStages.TRANSCRIBING]: t("STAGE_TRANSCRIBING"),
    [ProcessingStages.ANALYZING]: t("STAGE_ANALYZING"),
    [ProcessingStages.FINALIZING]: t("STAGE_FINALIZING"),
    // Legacy stages
    [ProcessingStages.TEXT_ANALYSIS]: t("STAGE_ANALYZING"),
    [ProcessingStages.AUDIO_ANALYSIS]: t("STAGE_ANALYZING"),
    [ProcessingStages.SUMMARIZATION]: t("STAGE_ANALYZING"),
  };
  return stageLabels[stage] || stage;
}

// Determine which tabs are available based on processing stage AND actual data presence.
// Data-driven: each tab unlocks as soon as its data is available, supporting
// incremental results from parallel backend processing.
function getTabAvailability(
  stage: TProcessingStage | null | undefined,
  status: string | undefined,
  data: { transcript: string; textAnalysis: string; voiceAnalysis: string; meetingSummary: string; }
): {
  transcript: boolean;
  text: boolean;
  voice: boolean;
  summary: boolean;
} {
  // If status is complete or draft, all tabs are available
  if (status === MeetingRecordStatus.COMPLETE || status === MeetingRecordStatus.DRAFT) {
    return { transcript: true, text: true, voice: true, summary: true };
  }

  // During processing or failed: unlock each tab as its data becomes available.
  // The backend saves each analysis result individually, so the frontend can
  // detect partial progress via polling.
  return {
    transcript: !!data.transcript,
    text: !!data.textAnalysis,
    voice: !!data.voiceAnalysis,
    summary: !!data.meetingSummary,
  };
}

function SummaryMeeting() {
  const location = useLocation();
  const navigate = useNavigate();
  const { recordNumber: recordNumberParam } = useParams<{ recordNumber: string }>();
  
  // Get Guid from location state or sessionStorage
  const storedDetails = getStoredUserDetails();
  const Guid = location.state?.Guid || location.state?.UserDetails?.Guid || storedDetails?.Guid;
  const userId = location.state?.UsersId || location.state?.UserDetails?.UsersId || storedDetails?.UsersId;
  const recordNumber = recordNumberParam ? parseInt(recordNumberParam, 10) : null;

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<
    "transcript" | "summary" | "text" | "voice"
  >("summary");
  const [meetingSummary, setMeetingSummary] = useState<string>("");
  const [structuredSummary, setStructuredSummary] = useState<TStructuredMeetingSummary | null>(null);
  const [editableSummary, setEditableSummary] = useState<TStructuredMeetingSummary | null>(null);
  const [transcript, setTranscript] = useState<string>("");
  const [textAnalysis, setTextAnalysis] = useState<string>("");
  const [voiceAnalysis, setVoiceAnalysis] = useState<string>("");
  const [mediaType, setMediaType] = useState<string>("");
  const [optionForReport, setOptionForReport] = useState<string>("");
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [showQuestionnaireModal, setShowQuestionnaireModal] = useState<boolean>(false);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [textData, setTextData] = useState<TTextAnalysis | null>(null);
  const [voiceData, setVoiceData] = useState<any>(null);
  const isMobile = useMediaQuery('(max-width: 480px)');

  // Processing state
  const [processingStage, setProcessingStage] = useState<TProcessingStage | null>(null);
  const [recordStatus, setRecordStatus] = useState<string>(MeetingRecordStatus.PROCESSING);

  // Record metadata
  const [recordDuration, setRecordDuration] = useState<number | null>(null);
  const [recordDate, setRecordDate] = useState<string>("");
  const [recordTime, setRecordTime] = useState<string>("");
  const [recordSubject, setRecordSubject] = useState<string>("");
  const [therapistName, setTherapistName] = useState<string>("");
  const [appointmentId, setAppointmentId] = useState<number | null>(null);
  const [appointmentStartTime, setAppointmentStartTime] = useState<string | null>(null);
  const [appointmentDate, setAppointmentDate] = useState<string | null>(null);
  const [therapistQuestionnaireCompaniesScoreUsersId, setTherapistQuestionnaireCompaniesScoreUsersId] =
    useState<number | null>(null);
  const [recordCycle, setRecordCycle] = useState<number | null>(null);
  const [recordQuestionnaireCompaniesId, setRecordQuestionnaireCompaniesId] =
    useState<number | undefined>(undefined);

  // Reprocessing state
  const [reprocessingStage, setReprocessingStage] = useState<string | null>(null);

  // Mutation for fetching record data
  const [fetchRecords] = useGetUserMeetingRecordsMutation();
  const [updateSummary, { isLoading: isSaving }] = useUpdateMeetingSummaryMutation();
  const [exportPdf, { isLoading: isExporting }] = useExportMeetingSummaryToPdfMutation();
  const [reprocessStage] = useReprocessMeetingRecordStageMutation();

  // Helper to update all state from a record
  const updateStateFromRecord = (record: any) => {
    const parsed = parseRecordData(record);
    setMeetingSummary(parsed.meetingSummary);
    setStructuredSummary(parsed.structuredSummary);
    setTranscript(parsed.transcript);
    setTextAnalysis(parsed.textAnalysis);
    setVoiceAnalysis(parsed.voiceAnalysis);
    setOptionForReport(parsed.optionForReport);
    setTextData(parsed.textData);
    setVoiceData(parsed.voiceData);
    setProcessingStage(parsed.processingStage);
    setRecordStatus(parsed.recordStatus);
    setRecordDuration(parsed.recordDuration);
    setRecordDate(parsed.recordDate);
    setRecordTime(parsed.recordTime);
    setRecordSubject(parsed.recordSubject);
    setTherapistName(parsed.therapistName);
    setMediaType(parsed.mediaType);
    setAppointmentId(parsed.appointmentId);
    setAppointmentStartTime(parsed.appointmentStartTime);
    setAppointmentDate(parsed.appointmentDate);
    setTherapistQuestionnaireCompaniesScoreUsersId(
      parsed.therapistQuestionnaireCompaniesScoreUsersId
    );
    setRecordCycle(parsed.cycle);
    setRecordQuestionnaireCompaniesId(parsed.questionnaireCompaniesId);
  };

  // Fetch record data on mount and when recordNumber changes
  useEffect(() => {
    if (!Guid || !recordNumber) {
      setLoadError("Missing record information");
      setIsLoading(false);
      return;
    }

    const loadRecord = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const response = await fetchRecords(Guid);
        if ("data" in response && response.data?.records && Array.isArray(response.data.records)) {
          const record = response.data.records.find(
            (r: any) => r.recordNumber === recordNumber
          );
          if (record) {
            updateStateFromRecord(record);
          } else {
            setLoadError("Record not found");
          }
        } else {
          setLoadError("Failed to load record");
        }
      } catch {
        setLoadError("Error loading record");
      } finally {
        setIsLoading(false);
      }
    };

    loadRecord();
  }, [Guid, recordNumber, fetchRecords]);

  // Polling effect for processing records - fetch updated data every 5 seconds
  useEffect(() => {
    if (recordStatus !== MeetingRecordStatus.PROCESSING || !Guid || !recordNumber) {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await fetchRecords(Guid);
        if ("data" in response && response.data?.records && Array.isArray(response.data.records)) {
          const record = response.data.records.find(
            (r: any) => r.recordNumber === recordNumber
          );
          if (record) {
            updateStateFromRecord(record);
          }
        }
      } catch {
        // Polling failed silently, will retry on next interval
      }
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [recordStatus, Guid, recordNumber, fetchRecords]);

  // Calculate tab availability based on processing stage and actual data presence
  const tabAvailability = getTabAvailability(processingStage, recordStatus, {
    transcript,
    textAnalysis,
    voiceAnalysis,
    meetingSummary,
  });
  const isProcessing = recordStatus === MeetingRecordStatus.PROCESSING;
  const isFailed = recordStatus === MeetingRecordStatus.FAILED;
  const currentStep = getCurrentStep(processingStage);
  const totalSteps = 4;

  // Disable action buttons (export, edit, etc.) until the meeting summary phase is done.
  // The backend saves the summary independently; once meetingSummary is populated, the buttons unlock.
  const isActionButtonsDisabled = isProcessing && !meetingSummary;

  // Handler for reprocessing a single stage
  const handleReprocessStage = async (stage: "transcription" | "textAnalysis" | "audioAnalysis" | "summary") => {
    if (!userId || !recordNumber || reprocessingStage) return;
    setReprocessingStage(stage);
    try {
      const result = await reprocessStage({ userGuid: Guid, recordNumber, stage }).unwrap();
      if (result.success && Guid) {
        // Refetch the record to update UI with new data
        const response = await fetchRecords(Guid);
        if ("data" in response && response.data?.records && Array.isArray(response.data.records)) {
          const updated = response.data.records.find(
            (r: any) => r.recordNumber === recordNumber
          );
          if (updated) {
            updateStateFromRecord(updated);
          }
        }
      }
    } catch (err) {
      console.error(`Failed to reprocess ${stage}:`, err);
    } finally {
      setReprocessingStage(null);
    }
  };

  async function handleExportPdf() {
    if (!userId || !recordNumber) {
      alert('Missing user or record information');
      return;
    }

    try {
      const currentLanguage = i18n.language || 'en';
      
      const result = await exportPdf({
        userGuid: Guid,
        recordNumber: recordNumber,
        language: currentLanguage,
      }).unwrap();

      if (result.success && result.pdfBase64) {
        // Download the PDF
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${result.pdfBase64}`;
        link.download = result.fileName || `meeting_summary_${recordNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert(result.errorMessage || 'Failed to generate PDF');
      }
    } catch (error: any) {
      alert(error?.data?.errorMessage || error?.message || 'Export failed. Please try again.');
    }
  }

  function handleBackToRecordings() {
    navigate("/users/recordTable", {
      state: {
        UserDetails: {
          Guid: Guid,
          UsersId: userId,
        },
      },
    });
  }

  // Handler for export to action report
  function handleExportActionReport() {
    if (therapistQuestionnaireCompaniesScoreUsersId != null) {
      navigate("/users/report", {
        state: {
          UserDetails: {
            Guid: Guid,
            UsersId: userId,
          },
          TherapistDetails: {
            questionnaireCompaniesScoreUsersId: therapistQuestionnaireCompaniesScoreUsersId,
            ...(recordCycle != null ? { TherapistCycle: recordCycle } : {}),
            appointmentsId: appointmentId,
          },
        },
      });
      return;
    }
    setShowQuestionnaireModal(true);
  }

  // Handler for when auto-fill is complete - navigate to action report page with pre-filled data
  function handleAutoFillComplete(
    questionnaireId: number,
    questionnaireCompaniesId: number,
    autoFilledData: TAutoFillActionReportResponse
  ) {
    setShowQuestionnaireModal(false);
    navigate("/users/report", {
      state: {
        UserDetails: {
          Guid: Guid,
          UsersId: userId,
        },
        TherapistDetails: {
          TherapistQuestionnaireId: questionnaireId,
          QuestionnaireCompaniesId: questionnaireCompaniesId,
          appointmentsId: appointmentId,
          ...(recordCycle != null ? { TherapistCycle: recordCycle } : {}),
        },
        autoFilledData: autoFilledData,
        meetingSummary: meetingSummary,
      },
    });
  }

  async function copyMeetingSummary() {
    try {
      await navigator.clipboard.writeText(meetingSummary);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500); 
    } catch {
      // Fallback for older browsers or when clipboard API is not available
      const ta = document.createElement("textarea");
      ta.value = meetingSummary;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try {
        const range = document.createRange();
        range.selectNodeContents(ta);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      } catch {
        // Selection API fallback failed, but ta.select() should still work
      }
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }

  // Get the meeting subject - prefer AI-extracted subject from structured summary
  const displaySubject = structuredSummary?.subject || recordSubject || "";

  // Handler for toggling edit mode
  async function handleToggleEdit() {
    if (!isEditing) {
      // Entering edit mode - create a deep copy of structured summary
      // and ensure nested objects exist
      if (structuredSummary) {
        const copy = JSON.parse(JSON.stringify(structuredSummary));
        // Initialize nested objects if they don't exist
        if (!copy.patientStatus) {
          copy.patientStatus = {
            currentCondition: "",
            progressNotes: "",
            concerns: ""
          };
        }
        if (!copy.treatmentPlan) {
          copy.treatmentPlan = {
            current: "",
            modifications: "",
            nextSteps: ""
          };
        }
        setEditableSummary(copy);
      } else {
        // Initialize empty structure if no summary exists
        setEditableSummary({
          subject: "",
          primaryFindings: [],
          decisions: [],
          recommendations: [],
          actionItems: [],
          patientStatus: {
            currentCondition: "",
            progressNotes: "",
            concerns: ""
          },
          treatmentPlan: {
            current: "",
            modifications: "",
            nextSteps: ""
          },
          nextAppointment: "",
          additionalNotes: ""
        });
      }
      setSaveSuccess(false);
      setSaveError(null);
    } else {
      // Saving changes - update both structuredSummary and meetingSummary
      if (editableSummary && recordNumber && userId) {
        const updatedSummary = JSON.stringify(editableSummary, null, 2);
        
        try {
          // Call the API to save the updated summary
          const response = await updateSummary({
            userGuid: Guid,
            recordNumber: recordNumber,
            meetingSummary: updatedSummary,
          }).unwrap();
          
          // Update local state on success
          setStructuredSummary(editableSummary);
          setMeetingSummary(updatedSummary);
          setSaveSuccess(true);
          setSaveError(null);
          
          // Show success message briefly
          setTimeout(() => setSaveSuccess(false), 3000);
        } catch (error: any) {
          // Handle errors
          const errorMessage = error?.data?.message || error?.message || t("SAVE_FAILED");
          setSaveError(errorMessage);
          // Don't exit edit mode on error
          return;
        }
      }
    }
    setIsEditing(!isEditing);
  }

  // Helper to update editable summary fields
  function updateEditableField(path: string[], value: any) {
    if (!editableSummary) return;
    const updated = JSON.parse(JSON.stringify(editableSummary));
    let current: any = updated;
    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }
    current[path[path.length - 1]] = value;
    setEditableSummary(updated);
  }

  // Helper to update array items in editable summary
  function updateEditableArrayItem(arrayPath: string[], index: number, value: string) {
    if (!editableSummary) return;
    const updated = JSON.parse(JSON.stringify(editableSummary));
    let current: any = updated;
    for (const key of arrayPath) {
      current = current[key];
    }
    current[index] = value;
    setEditableSummary(updated);
  }

  // Helper to remove array item
  function removeEditableArrayItem(arrayPath: string[], index: number) {
    if (!editableSummary) return;
    const updated = JSON.parse(JSON.stringify(editableSummary));
    let current: any = updated;
    for (const key of arrayPath) {
      current = current[key];
    }
    current.splice(index, 1);
    setEditableSummary(updated);
  }

  // Helper to add array item
  function addEditableArrayItem(arrayPath: string[]) {
    if (!editableSummary) return;
    const updated = JSON.parse(JSON.stringify(editableSummary));
    let current: any = updated;
    for (const key of arrayPath) {
      if (!current[key]) {
        current[key] = [];
      }
      current = current[key];
    }
    current.push("");
    setEditableSummary(updated);
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className={styles.pageContainer} dir={i18n.dir()}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>{t("LOADING")}</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (loadError) {
    return (
      <div className={styles.pageContainer} dir={i18n.dir()}>
        <div className={styles.errorContainer}>
          <p>{loadError}</p>
          <button onClick={handleBackToRecordings}>{t("BACK_TO_RECORDINGS")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer} dir={i18n.dir()}>
      <div className={styles.tabsContainer}>
        {/* Purple Gradient Header */}
        <div className={styles.detailHeader}>
          <div>
            <h2>{t("MEETING")} - {formatDate(recordDate)}</h2>
            <p style={{ opacity: 0.9, marginTop: "5px" }}>
              {displaySubject || ""}
            </p>
          </div>
          <button className={styles.closeBtn} onClick={handleBackToRecordings}>
            ✕
          </button>
        </div>

        {/* Body Content */}
        <div className={styles.detailBody}>
          {/* Meeting Info Cards */}
          <div className={styles.infoGrid}>
            <div className={styles.infoCard}>
              <div className={styles.infoCardLabel}>{t("RECORDINGS_DATE_TIME")}</div>
              <div className={styles.infoCardValue}>
                {formatDate(recordDate)}{recordTime ? `, ${recordTime}` : ""}
              </div>
            </div>
            <div className={styles.infoCard}>
              <div className={styles.infoCardLabel}>{t("RECORDINGS_DURATION")}</div>
              <div className={styles.infoCardValue}>
                {recordDuration != null ? (
                  formatDuration(recordDuration)
                ) : isProcessing ? (
                  <span className={styles.infoCardSpinner} />
                ) : (
                  "—"
                )}
              </div>
            </div>
            <div className={styles.infoCard}>
              <div className={styles.infoCardLabel}>{t("THERAPIST")}</div>
              <div className={styles.infoCardValue}>{therapistName || "—"}</div>
            </div>
            <div className={styles.infoCard}>
              <div className={styles.infoCardLabel}>{t("MEETING_SUBJECT")}</div>
              <div className={styles.infoCardValue}>{displaySubject || mediaType || "—"}</div>
            </div>
            <div className={styles.infoCard}>
              <div className={styles.infoCardLabel}>{t("RECORDING_LINKAGE") || "שיוך פגישה"}</div>
              <div className={styles.infoCardValue}>
                {appointmentId
                  ? `${t("LINKED_TO_APPOINTMENT") || "משויך לתור"} — ${appointmentDate ? formatDate(appointmentDate) : ""}${appointmentStartTime ? `, ${appointmentStartTime}` : ""}`
                  : t("NOT_LINKED") || "לא משויך"
                }
              </div>
            </div>
          </div>

          {/* Processing Banner - shown when record is still being processed */}
          {isProcessing && (
            <div className={styles.processingBanner}>
              <span className={styles.processingBannerIcon}>⏳</span>
              <div className={styles.processingBannerText}>
                <div className={styles.processingBannerTitle}>
                  {t("PROCESSING_IN_PROGRESS")}
                </div>
                <div className={styles.processingBannerSubtitle}>
                  {getStageLabel(processingStage)} ({currentStep}/{totalSteps})
                </div>
              </div>
              <div className={styles.processingBannerStepper}>
                {[1, 2, 3, 4, 5, 6].map((step) => (
                  <div
                    key={step}
                    className={`${styles.processingBannerStep} ${
                      step <= currentStep ? styles.processingBannerStepActive : ''
                    } ${step === currentStep ? styles.processingBannerStepCurrent : ''}`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Failed Banner - shown when processing failed */}
          {isFailed && (
            <div className={styles.failedBanner}>
              <span className={styles.failedBannerIcon}>❌</span>
              <div className={styles.failedBannerText}>
                <div className={styles.failedBannerTitle}>
                  {t("PROCESSING_FAILED")}
                </div>
                <div className={styles.failedBannerSubtitle}>
                  {t("PROCESSING_FAILED_CONTACT_SUPPORT")}
                </div>
              </div>
            </div>
          )}

      <div className={styles.tabsHeader}>
        <div
          className={`${styles.tabItem} ${
            activeTab === "voice" ? styles.active : ""
          } ${!tabAvailability.voice && isProcessing ? styles.tabItemDisabled : ""}`}
          onClick={() => setActiveTab("voice")}
        >
          {t("VOICE_ANALSIS")} 🎵
          {((!tabAvailability.voice && isProcessing) || reprocessingStage === "audioAnalysis") && <span className={styles.tabProcessingBadge} />}
        </div>
        <div
          className={`${styles.tabItem} ${
            activeTab === "text" ? styles.active : ""
          } ${!tabAvailability.text && isProcessing ? styles.tabItemDisabled : ""}`}
          onClick={() => setActiveTab("text")}
        >
          {t("TEXT_ANALSIS")} 📊
          {((!tabAvailability.text && isProcessing) || reprocessingStage === "textAnalysis") && <span className={styles.tabProcessingBadge} />}
        </div>
        <div
          className={`${styles.tabItem} ${
            activeTab === "summary" ? styles.active : ""
          } ${!tabAvailability.summary && isProcessing ? styles.tabItemDisabled : ""}`}
          onClick={() => setActiveTab("summary")}
        >
          {t("MEETING_SUMMARY")} 📋
          {((!tabAvailability.summary && isProcessing) || reprocessingStage === "summary") && <span className={styles.tabProcessingBadge} />}
        </div>
        <div
          className={`${styles.tabItem} ${
            activeTab === "transcript" ? styles.active : ""
          } ${!tabAvailability.transcript && isProcessing ? styles.tabItemDisabled : ""}`}
          onClick={() => setActiveTab("transcript")}
        >
          {t("TRANSCRIPTION")} 📝
          {((!tabAvailability.transcript && isProcessing) || reprocessingStage === "transcription") && <span className={styles.tabProcessingBadge} />}
        </div>
      </div>

      <div className={styles.tabContent} dir={i18n.dir()}>

        {activeTab === "voice" && (
          !tabAvailability.voice && isProcessing ? (
            <div className={styles.processingTabContent}>
              <div className={styles.processingSpinner} />
              <div className={styles.processingMessage}>{t("TAB_PROCESSING")}</div>
              <div className={styles.processingStage}>{t("STAGE_AUDIO_ANALYSIS")}</div>
            </div>
          ) : (
            <div className={styles.tabText}>
              <VoiceAnalysisInPopup
                data={voiceData}
                onRetry={() => handleReprocessStage("audioAnalysis")}
                isRetrying={reprocessingStage === "audioAnalysis"}
              />
            </div>
          )
        )}

        {activeTab === "text" && (
          !tabAvailability.text && isProcessing ? (
            <div className={styles.processingTabContent}>
              <div className={styles.processingSpinner} />
              <div className={styles.processingMessage}>{t("TAB_PROCESSING")}</div>
              <div className={styles.processingStage}>{t("STAGE_TEXT_ANALYSIS")}</div>
            </div>
          ) : (
            <div className={styles.tabText}>
              <div>
                <TextAnalysisInPopup
                  data={textData}
                  onRetry={() => handleReprocessStage("textAnalysis")}
                  isRetrying={reprocessingStage === "textAnalysis"}
                />
              </div>
            </div>
          )
        )}

        {activeTab === "summary" && (
          !tabAvailability.summary && isProcessing ? (
            <div className={styles.processingTabContent}>
              <div className={styles.processingSpinner} />
              <div className={styles.processingMessage}>{t("TAB_PROCESSING")}</div>
              <div className={styles.processingStage}>{t("STAGE_SUMMARIZATION")}</div>
            </div>
          ) : (
          <div className={styles.summaryTab} dir={i18n.dir()}>
            <div className={styles.summaryContent}>
              {/* Save Success Banner */}
              {saveSuccess && (
                <div className={styles.saveSuccessBanner} dir={i18n.dir()}>
                  <span>✅</span>
                  <span>{t("SAVE_SUCCESS")}</span>
                </div>
              )}
              
              {/* Save Error Banner */}
              {saveError && (
                <div className={styles.saveErrorBanner} dir={i18n.dir()}>
                  <span>❌</span>
                  <span>{saveError}</span>
                </div>
              )}
              
              {/* Edit Mode Indicator */}
              {isEditing && (
                <div className={styles.editModeIndicator}>
                  <span>✏️</span>
                  <span>{t("EDIT_MODE_ACTIVE")}</span>
                </div>
              )}
              {(isEditing ? editableSummary : structuredSummary) ? (
                <div className={`${styles.summaryBox} ${isEditing ? styles.editMode : ''}`}>
                  <div className={styles.summaryBoxInner}>
                  {/* Meeting Subject */}
                  <div className={styles.summarySection}>
                    <h4 className={styles.summarySectionTitle}>
                      <span>🎯</span>
                      {t("MEETING_SUBJECT")}
                    </h4>
                    {isEditing ? (
                      <input
                        type="text"
                        dir={i18n.dir()}
                        className={styles.editableInput}
                        value={editableSummary?.subject || ""}
                        onChange={(e) => updateEditableField(["subject"], e.target.value)}
                        placeholder={t("MEETING_SUBJECT")}
                      />
                    ) : structuredSummary?.subject ? (
                      <div className={styles.subjectHighlight}>
                        {structuredSummary?.subject}
                      </div>
                    ) : (
                      <div className={styles.emptyStateMessage}>
                        <span className={styles.emptyStateIcon}>ℹ️</span>
                        <span>{t("NO_SUBJECT_GENERATED")}</span>
                      </div>
                    )}
                  </div>

                  {/* Primary Findings */}
                  <div className={styles.summarySection}>
                    <h4 className={styles.summarySectionTitle}>
                      <span>📊</span>
                      {t("PRIMARY_FINDINGS")}
                      {isEditing && (
                        <button 
                          className={styles.addItemBtn}
                          onClick={() => addEditableArrayItem(["primaryFindings"])}
                        >
                          + {t("ADD_ITEM")}
                        </button>
                      )}
                    </h4>
                    {((isEditing ? editableSummary?.primaryFindings : structuredSummary?.primaryFindings) || []).length > 0 ? (
                      <ul className={styles.summaryList}>
                        {(isEditing ? editableSummary?.primaryFindings : structuredSummary?.primaryFindings)?.map((finding, idx) => (
                          <li key={idx} className={styles.summaryListItem}>
                            {isEditing ? (
                              <div className={styles.editableListItem}>
                                <textarea
                                  dir={i18n.dir()}
                                  className={styles.editableTextarea}
                                  value={finding}
                                  onChange={(e) => updateEditableArrayItem(["primaryFindings"], idx, e.target.value)}
                                  rows={2}
                                />
                                <button
                                  className={styles.removeItemBtn}
                                  onClick={() => removeEditableArrayItem(["primaryFindings"], idx)}
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              finding
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className={styles.emptyStateMessage}>
                        <span className={styles.emptyStateIcon}>ℹ️</span>
                        <span>{t("NO_FINDINGS_GENERATED")}</span>
                      </div>
                    )}
                  </div>

                  {/* Clinical Decisions */}
                  <div className={styles.summarySection}>
                    <h4 className={styles.summarySectionTitle}>
                      <span>💊</span>
                      {t("CLINICAL_DECISIONS")}
                      {isEditing && (
                        <button 
                          className={styles.addItemBtn}
                          onClick={() => addEditableArrayItem(["decisions"])}
                        >
                          + {t("ADD_ITEM")}
                        </button>
                      )}
                    </h4>
                    {((isEditing ? editableSummary?.decisions : structuredSummary?.decisions) || []).length > 0 ? (
                      <ul className={styles.summaryList}>
                        {(isEditing ? editableSummary?.decisions : structuredSummary?.decisions)?.map((decision, idx) => (
                          <li key={idx} className={styles.summaryListItem}>
                            {isEditing ? (
                              <div className={styles.editableListItem}>
                                <textarea
                                  dir={i18n.dir()}
                                  className={styles.editableTextarea}
                                  value={decision}
                                  onChange={(e) => updateEditableArrayItem(["decisions"], idx, e.target.value)}
                                  rows={2}
                                />
                                <button
                                  className={styles.removeItemBtn}
                                  onClick={() => removeEditableArrayItem(["decisions"], idx)}
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              decision
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className={styles.emptyStateMessage}>
                        <span className={styles.emptyStateIcon}>ℹ️</span>
                        <span>{t("NO_DECISIONS_GENERATED")}</span>
                      </div>
                    )}
                  </div>

                  {/* Recommendations to Patient */}
                  <div className={styles.summarySection}>
                    <h4 className={styles.summarySectionTitle}>
                      <span>📝</span>
                      {t("RECOMMENDATIONS_TO_PATIENT")}
                      {isEditing && (
                        <button 
                          className={styles.addItemBtn}
                          onClick={() => addEditableArrayItem(["recommendations"])}
                        >
                          + {t("ADD_ITEM")}
                        </button>
                      )}
                    </h4>
                    {((isEditing ? editableSummary?.recommendations : structuredSummary?.recommendations) || []).length > 0 ? (
                      <ul className={styles.summaryList}>
                        {(isEditing ? editableSummary?.recommendations : structuredSummary?.recommendations)?.map((rec, idx) => (
                          <li key={idx} className={styles.summaryListItem}>
                            {isEditing ? (
                              <div className={styles.editableListItem}>
                                <textarea
                                  dir={i18n.dir()}
                                  className={styles.editableTextarea}
                                  value={rec}
                                  onChange={(e) => updateEditableArrayItem(["recommendations"], idx, e.target.value)}
                                  rows={2}
                                />
                                <button
                                  className={styles.removeItemBtn}
                                  onClick={() => removeEditableArrayItem(["recommendations"], idx)}
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              rec
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className={styles.emptyStateMessage}>
                        <span className={styles.emptyStateIcon}>ℹ️</span>
                        <span>{t("NO_RECOMMENDATIONS_GENERATED")}</span>
                      </div>
                    )}
                  </div>

                  {/* Action Items / Follow-ups */}
                  <div className={styles.summarySection}>
                    <h4 className={styles.summarySectionTitle}>
                      <span>✅</span>
                      {t("ACTION_ITEMS")}
                      {isEditing && (
                        <button 
                          className={styles.addItemBtn}
                          onClick={() => addEditableArrayItem(["actionItems"])}
                        >
                          + {t("ADD_ITEM")}
                        </button>
                      )}
                    </h4>
                    {((isEditing ? editableSummary?.actionItems : structuredSummary?.actionItems) || []).length > 0 ? (
                      <ul className={styles.summaryList}>
                        {(isEditing ? editableSummary?.actionItems : structuredSummary?.actionItems)?.map((item, idx) => (
                          <li key={idx} className={styles.summaryListItem}>
                            {isEditing ? (
                              <div className={styles.editableListItem}>
                                <textarea
                                  dir={i18n.dir()}
                                  className={styles.editableTextarea}
                                  value={item}
                                  onChange={(e) => updateEditableArrayItem(["actionItems"], idx, e.target.value)}
                                  rows={2}
                                />
                                <button
                                  className={styles.removeItemBtn}
                                  onClick={() => removeEditableArrayItem(["actionItems"], idx)}
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              item
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className={styles.emptyStateMessage}>
                        <span className={styles.emptyStateIcon}>ℹ️</span>
                        <span>{t("NO_ACTION_ITEMS_GENERATED")}</span>
                      </div>
                    )}
                  </div>

                  {/* Patient Status & Treatment Plan - Side by side */}
                  <div className={styles.statusGrid}>
                    {/* Patient Status */}
                    <div className={styles.summarySection}>
                      <h4 className={styles.summarySectionTitle}>
                        <span>🩺</span>
                        {t("PATIENT_STATUS")}
                      </h4>
                      {hasPatientStatusContent(isEditing ? editableSummary?.patientStatus : structuredSummary?.patientStatus) ? (
                        <div className={styles.statusCard}>
                          {(isEditing || structuredSummary?.patientStatus?.currentCondition) && (
                            <div className={styles.statusCardItem}>
                              <div className={styles.statusCardLabel}>{t("CURRENT_CONDITION")}</div>
                              {isEditing ? (
                                <textarea
                                  dir={i18n.dir()}
                                className={styles.editableTextarea}
                                  value={editableSummary?.patientStatus?.currentCondition || ""}
                                  onChange={(e) => updateEditableField(["patientStatus", "currentCondition"], e.target.value)}
                                  rows={2}
                                  placeholder={t("CURRENT_CONDITION")}
                                />
                              ) : (
                                <div className={styles.statusCardValue}>
                                  {structuredSummary?.patientStatus?.currentCondition}
                                </div>
                              )}
                            </div>
                          )}
                          {(isEditing || structuredSummary?.patientStatus?.progressNotes) && (
                            <div className={styles.statusCardItem}>
                              <div className={styles.statusCardLabel}>{t("PROGRESS_NOTES")}</div>
                              {isEditing ? (
                                <textarea
                                  dir={i18n.dir()}
                                className={styles.editableTextarea}
                                  value={editableSummary?.patientStatus?.progressNotes || ""}
                                  onChange={(e) => updateEditableField(["patientStatus", "progressNotes"], e.target.value)}
                                  rows={2}
                                  placeholder={t("PROGRESS_NOTES")}
                                />
                              ) : (
                                <div className={styles.statusCardValue}>
                                  {structuredSummary?.patientStatus?.progressNotes}
                                </div>
                              )}
                            </div>
                          )}
                          {(isEditing || structuredSummary?.patientStatus?.concerns) && (
                            <div className={styles.statusCardItem}>
                              <div className={styles.statusCardLabel}>{t("CONCERNS")}</div>
                              {isEditing ? (
                                <textarea
                                  dir={i18n.dir()}
                                className={styles.editableTextarea}
                                  value={editableSummary?.patientStatus?.concerns || ""}
                                  onChange={(e) => updateEditableField(["patientStatus", "concerns"], e.target.value)}
                                  rows={2}
                                  placeholder={t("CONCERNS")}
                                />
                              ) : (
                                <div className={styles.statusCardValue}>
                                  {structuredSummary?.patientStatus?.concerns}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className={styles.emptyStateMessage}>
                          <span className={styles.emptyStateIcon}>ℹ️</span>
                          <span>{t("NO_PATIENT_STATUS_GENERATED")}</span>
                        </div>
                      )}
                    </div>

                    {/* Treatment Plan */}
                    <div className={styles.summarySection}>
                      <h4 className={styles.summarySectionTitle}>
                        <span>📋</span>
                        {t("TREATMENT_PLAN")}
                      </h4>
                      {hasTreatmentPlanContent(isEditing ? editableSummary?.treatmentPlan : structuredSummary?.treatmentPlan) ? (
                        <div className={styles.statusCard}>
                          {(isEditing || structuredSummary?.treatmentPlan?.current) && (
                            <div className={styles.statusCardItem}>
                              <div className={styles.statusCardLabel}>{t("CURRENT_TREATMENT")}</div>
                              {isEditing ? (
                                <textarea
                                  dir={i18n.dir()}
                                className={styles.editableTextarea}
                                  value={editableSummary?.treatmentPlan?.current || ""}
                                  onChange={(e) => updateEditableField(["treatmentPlan", "current"], e.target.value)}
                                  rows={2}
                                  placeholder={t("CURRENT_TREATMENT")}
                                />
                              ) : (
                                <div className={styles.statusCardValue}>
                                  {structuredSummary?.treatmentPlan?.current}
                                </div>
                              )}
                            </div>
                          )}
                          {(isEditing || structuredSummary?.treatmentPlan?.modifications) && (
                            <div className={styles.statusCardItem}>
                              <div className={styles.statusCardLabel}>{t("MODIFICATIONS")}</div>
                              {isEditing ? (
                                <textarea
                                  dir={i18n.dir()}
                                className={styles.editableTextarea}
                                  value={editableSummary?.treatmentPlan?.modifications || ""}
                                  onChange={(e) => updateEditableField(["treatmentPlan", "modifications"], e.target.value)}
                                  rows={2}
                                  placeholder={t("MODIFICATIONS")}
                                />
                              ) : (
                                <div className={styles.statusCardValue}>
                                  {structuredSummary?.treatmentPlan?.modifications}
                                </div>
                              )}
                            </div>
                          )}
                          {(isEditing || structuredSummary?.treatmentPlan?.nextSteps) && (
                            <div className={styles.statusCardItem}>
                              <div className={styles.statusCardLabel}>{t("NEXT_STEPS")}</div>
                              {isEditing ? (
                                <textarea
                                  dir={i18n.dir()}
                                className={styles.editableTextarea}
                                  value={editableSummary?.treatmentPlan?.nextSteps || ""}
                                  onChange={(e) => updateEditableField(["treatmentPlan", "nextSteps"], e.target.value)}
                                  rows={2}
                                  placeholder={t("NEXT_STEPS")}
                                />
                              ) : (
                                <div className={styles.statusCardValue}>
                                  {structuredSummary?.treatmentPlan?.nextSteps}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className={styles.emptyStateMessage}>
                          <span className={styles.emptyStateIcon}>ℹ️</span>
                          <span>{t("NO_TREATMENT_PLAN_GENERATED")}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Next Appointment */}
                  <div className={styles.summarySection}>
                    <h4 className={styles.summarySectionTitle}>
                      <span>📅</span>
                      {t("NEXT_APPOINTMENT")}
                    </h4>
                    {isEditing ? (
                      <input
                        type="text"
                        dir={i18n.dir()}
                        className={styles.editableInput}
                        value={editableSummary?.nextAppointment || ""}
                        onChange={(e) => updateEditableField(["nextAppointment"], e.target.value)}
                        placeholder={t("NEXT_APPOINTMENT")}
                      />
                    ) : structuredSummary?.nextAppointment ? (
                      <div className={styles.nextAppointmentBadge}>
                        <span>🗓️</span>
                        {structuredSummary?.nextAppointment}
                      </div>
                    ) : (
                      <div className={styles.emptyStateMessage}>
                        <span className={styles.emptyStateIcon}>ℹ️</span>
                        <span>{t("NO_NEXT_APPOINTMENT_GENERATED")}</span>
                      </div>
                    )}
                  </div>

                  {/* Additional Notes */}
                  <div className={styles.summarySection}>
                    <h4 className={styles.summarySectionTitle}>
                      <span>💡</span>
                      {t("ADDITIONAL_NOTES")}
                    </h4>
                    {isEditing ? (
                      <textarea
                        dir={i18n.dir()}
                                className={styles.editableTextarea}
                        value={editableSummary?.additionalNotes || ""}
                        onChange={(e) => updateEditableField(["additionalNotes"], e.target.value)}
                        rows={3}
                        placeholder={t("ADDITIONAL_NOTES")}
                      />
                    ) : structuredSummary?.additionalNotes ? (
                      <div className={styles.additionalNotes}>
                        {structuredSummary?.additionalNotes}
                      </div>
                    ) : (
                      <div className={styles.emptyStateMessage}>
                        <span className={styles.emptyStateIcon}>ℹ️</span>
                        <span>{t("NO_ADDITIONAL_NOTES_GENERATED")}</span>
                      </div>
                    )}
                  </div>
                  </div>
                </div>
              ) : (
                // Fallback to plain text summary
                <div className={styles.tabText}>
                  {meetingSummary ? (
                    <div className={styles.plainTextSummary}>{meetingSummary}</div>
                  ) : (
                    <div className={styles.emptyStateMessage}>
                      <span className={styles.emptyStateIcon}>ℹ️</span>
                      <span>{t("NO_SUMMARY_GENERATED")}</span>
                      <button
                        className={styles.retryStageBtn}
                        onClick={() => handleReprocessStage("summary")}
                        disabled={reprocessingStage === "summary"}
                      >
                        {reprocessingStage === "summary" ? (
                          <><span className={styles.retrySpinner} /> {t("RETRYING")}</>
                        ) : (
                          <>{t("RETRY_SUMMARY")}</>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          )
        )}

        {activeTab === "transcript" && (
          !tabAvailability.transcript && isProcessing ? (
            <div className={styles.processingTabContent}>
              <div className={styles.processingSpinner} />
              <div className={styles.processingMessage}>{t("TAB_PROCESSING")}</div>
              <div className={styles.processingStage}>{t("STAGE_TRANSCRIBING")}</div>
            </div>
          ) : (
            <div className={styles.tabText}>
              <div className={styles.transcriptContainer}>
                {formatTranscript(transcript).length > 0 ? (
                  formatTranscript(transcript)
                ) : (
                  <div className={styles.emptyStateMessage}>
                    <span className={styles.emptyStateIcon}>ℹ️</span>
                    <span>{t("NO_TRANSCRIPTION_GENERATED")}</span>
                    <button
                      className={styles.retryStageBtn}
                      onClick={() => handleReprocessStage("transcription")}
                      disabled={reprocessingStage === "transcription"}
                    >
                      {reprocessingStage === "transcription" ? (
                        <><span className={styles.retrySpinner} /> {t("RETRYING")}</>
                      ) : (
                        <>{t("RETRY_TRANSCRIPTION")}</>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )
        )}
      </div>
        </div> {/* End detailBody */}

        {/* Action Buttons Footer */}
        <div className={styles.actionButtons}>
          <button 
            className={styles.primaryBtn} 
            onClick={handleExportPdf} 
            disabled={isExporting || isActionButtonsDisabled}
            dir={i18n.dir()}
            title={isActionButtonsDisabled ? t("WAIT_FOR_SUMMARY") : ""}
          >
            {isExporting ? (
              <>
                <span className={styles.savingSpinner} />
                <span>{t("EXPORTING")}</span>
              </>
            ) : (
              <>
                <span>{t("EXPORT_TO_PDF")}</span>
              </>
            )}
          </button>
          <button 
            className={styles.primaryBtn} 
            onClick={handleExportActionReport}
            disabled={isActionButtonsDisabled}
            title={isActionButtonsDisabled ? t("WAIT_FOR_SUMMARY") : ""}
          >
            <span>{t("EXPORT_ACTION_REPORT")}</span>
          </button>
          <button
            className={`${styles.secondaryBtn} ${isEditing ? styles.saveBtn : ''}`}
            onClick={handleToggleEdit}
            disabled={isSaving || isActionButtonsDisabled}
            title={isActionButtonsDisabled ? t("WAIT_FOR_SUMMARY") : ""}
          >
            {isSaving ? (
              <>
                <span className={styles.savingSpinner} />
                <span>{t("SAVING")}</span>
              </>
            ) : (
              <>
                <span>{isEditing ? t("SAVE") : t("EDIT_SUMMARY")}</span>
              </>
            )}
          </button>
          {isEditing && (
            <button
              className={styles.secondaryBtn}
              onClick={() => {
                setEditableSummary(null);
                setIsEditing(false);
                setSaveError(null);
              }}
              disabled={isSaving}
            >
              <span>↩️</span>
              <span>{t("CANCEL")}</span>
            </button>
          )}
          <button className={styles.secondaryBtn} onClick={handleBackToRecordings}>
            <span>❌</span>
            <span>{t("CLOSE")}</span>
          </button>
        </div>
      </div> {/* End tabsContainer */}

      {/* Questionnaire Selection Modal for Auto-Fill */}
      {showQuestionnaireModal && (
        <Modal onClose={() => setShowQuestionnaireModal(false)} prevencherAnswer={true}>
          <QuestionnaireSelectionModal
            onClose={() => setShowQuestionnaireModal(false)}
            onComplete={handleAutoFillComplete}
            meetingSummary={meetingSummary}
            transcript={transcript}
            {...(recordQuestionnaireCompaniesId != null
              ? { questionnaireCompaniesId: recordQuestionnaireCompaniesId }
              : {})}
          />
        </Modal>
      )}
    </div> /* End pageContainer */
  );
}

export default SummaryMeeting;
